import React, { useState } from "react";
import {
  getAllocationTableData,
  riskAllocationMapping,
  useApiAugmentedScenariosCoefficients,
  useFutureScenarioIncome,
  useScenariosCoefficients,
} from "@iliotech/data-wire";
import {
  ScenarioCoefficientsGrouping,
  ScenarioValuesGrouping,
} from "../../../../generated-api-v3";

export const SCENARIO_CATEGORIES = [
  "Sector",
  "Asset Class",
  "Country",
  "Currency",
  // "Custodian",
];

export const useScenarioManager = (portfolioId: string) => {
  const [initialised, setInitialised] = React.useState(false);
  const [months, setMonths] = React.useState(0);
  const [liveSliderValues, setLiveSliderValues] = React.useState<RiskImpacts>({
    price: 0,
    yield: 0,
    fx: 0,
    volatility: 0,
  });
  const [validSliderValues, setValidSliderValues] = React.useState<RiskImpacts>(
    {
      price: 0,
      yield: 0,
      fx: 0,
      volatility: 0,
    }
  );
  const scenarioCoefficients = useScenariosCoefficients(portfolioId);
  const [shouldLoadAugmentedData, setShouldLoadAugmentedData] =
    React.useState(false);

  const futureIncome = useFutureScenarioIncome(portfolioId!, true, months);

  const apiAugmentedScenariosCoefficients =
    useApiAugmentedScenariosCoefficients(
      portfolioId!,
      {
        price: validSliderValues.price,
        volatility: validSliderValues.volatility,
        fx: validSliderValues.fx,
        yield: validSliderValues.yield,
        months: months,
      },
      shouldLoadAugmentedData
    );

  const coefficients = scenarioCoefficients?.data?.data;
  const requiresApiValues = coefficients?.requiresBackendCalculations ?? true;
  const totalMarketValueBase = requiresApiValues
    ? apiAugmentedScenariosCoefficients?.data?.data?.totalMarketValueBase ?? 1
    : scenarioCoefficients?.data?.data?.totalMarketValueBase ?? 1;

  const { affectedByYieldChange, affectedByVolatilityChange } =
    coefficients ?? {};

  const [canRequestData, setCanRequestData] = useState<boolean>(false);

  const [shouldResetPaging, setShouldResetPaging] = useState<boolean>(false);

  const [slidersDisabled, setSliderDisabled] = useState<boolean>(
    requiresApiValues && (apiAugmentedScenariosCoefficients?.isLoading ?? true)
  );

  const [selectedCategory, setSelectedCategory] = useState(
    SCENARIO_CATEGORIES[0]
  );

  const setValidSliderValue = (key: string, value: number) => {
    setValidSliderValues((p) => ({ ...p, [key]: value }));
  };

  const handleSetMonths = (value: number) => {
    setMonths(value);
    if (value > 0) {
      setCanRequestData(true);
    }
    else{
      setCanRequestData(false);
    }
  };

  const setLiveSliderValue = (key: string, value: number) => {
    setLiveSliderValues((p) => ({ ...p, [key]: value }));
    if (!requiresApiValues) {
      setValidSliderValue(key, value);
    }
  };

  const maps = React.useMemo(() => {
    const multipliers = {
      priceMultiplier: validSliderValues?.price,
      yieldMultiplier: validSliderValues?.yield,
      fxMultiplier: validSliderValues?.fx,
      volatilityMultiplier: validSliderValues?.volatility,
    };

    if (requiresApiValues) {
      return riskAllocationMapping(
        apiAugmentedScenariosCoefficients?.data?.data,
        true,
        multipliers,
        futureIncome?.data?.data
      );
    }
    return riskAllocationMapping(
      coefficients,
      false,
      multipliers,
      futureIncome?.data?.data
    );
  }, [
    requiresApiValues,
    validSliderValues,
    apiAugmentedScenariosCoefficients?.data?.data,
    coefficients,
    futureIncome,
  ]);

  const categoryMap = new Map([
    ...maps.sectorsMaps.categoryMap,
    ...maps.assetClassesMaps.categoryMap,
    ...maps.countriesMaps.categoryMap,
    ...maps.currenciesMaps.categoryMap,
    ...maps.custodiansMaps.categoryMap,
  ]);
  const AllocationMap = new Map([
    ...maps.sectorsMaps.allocationMap,
    ...maps.assetClassesMaps.allocationMap,
    ...maps.countriesMaps.allocationMap,
    ...maps.currenciesMaps.allocationMap,
    ...maps.custodiansMaps.allocationMap,
  ]);

  const selectedAllocation = AllocationMap.get(selectedCategory);

  const impacts = requiresApiValues
    ? getImpactsFromBackendData(
        selectedAllocation as any,
        futureIncome?.data?.data
      )
    : getImpactsFromCoefficients(
        selectedAllocation as any,
        validSliderValues,
        futureIncome?.data?.data
      );

  const {
    pnl: pnlImpact,
    fx: fxImpact,
    price: priceImpact,
    income: incomeImpact,
  } = impacts;

  const percentageImpact = ((pnlImpact ?? 0) / totalMarketValueBase) * 100;

  const handleChangeCategory = (event: any) => {
    setSelectedCategory(event.target.value);
    setShouldResetPaging(true);
  };

  const onChangeSlider = (field: keyof RiskImpacts) => (value: number) => {
    let hasChanged = Math.round(value) !== liveSliderValues[field];
    if (hasChanged) {
      setLiveSliderValue(field, Math.round(value));
    }
    if (
      !slidersDisabled ||
      !requiresApiValues ||
      (hasChanged && requiresApiValues)
    ) {
      setCanRequestData(true);
    }
  };

  const resetSliders = () => {
    setLiveSliderValues({
      price: 0,
      yield: 0,
      volatility: 0,
      fx: 0,
    });
    setValidSliderValues({
      price: 0,
      yield: 0,
      volatility: 0,
      fx: 0,
    });
    setMonths(0);
    setSliderDisabled(false);
    setCanRequestData(true);
  };

  const tableData = getAllocationTableData(
    selectedAllocation,
    requiresApiValues,
    {
      priceMultiplier: validSliderValues.price,
      fxMultiplier: validSliderValues.fx,
      yieldMultiplier: validSliderValues.yield,
      volatilityMultiplier: validSliderValues.volatility,
    }
  );

  const refetchData = () => {
    setInitialised(true);
    if (canRequestData && requiresApiValues) {
      setValidSliderValues(liveSliderValues);
      setSliderDisabled(true);
      setCanRequestData(false);
      setShouldLoadAugmentedData(true);
      if (!scenarioCoefficients?.data?.data) {
        scenarioCoefficients.refetch();
      }
    }
  };

  return {
    liveSliderValues,
    validSliderValues,
    resetSliders,
    tableData,
    refetchData,
    shouldResetPaging,
    setShouldResetPaging,
    selectedAllocation,
    handleChangeCategory,
    onChangeSlider,
    canRequestData,
    setCanRequestData,
    slidersDisabled,
    selectedCategory,
    requiresApiValues,
    initialised,
    setInitialised,
    affectedByYieldChange,
    affectedByVolatilityChange,
    backendCalculatedDataLoading: apiAugmentedScenariosCoefficients.isLoading,
    impacts: {
      pnlImpact: pnlImpact ?? 0,
      priceImpact,
      fxImpact,
      incomeImpact: incomeImpact ?? 0,
    },
    percentageImpact,
    categoryMap,
    months,
    setMonths: handleSetMonths,
  };
};

function getImpactsFromBackendData(
  allocation: ScenarioValuesGrouping | undefined,
  futureIncome?: any
) {
  const impacts: ImpactTotals & { pnl?: number; income?: number } = {
    price: allocation?.totalPriceValue ?? 0,
    fx: allocation?.totalFxValue ?? 0,
    income: 0,
  };

  // Calculate income impact from future income data
  if (futureIncome) {
    const incomeData =
      futureIncome.sectors ||
      futureIncome.assets ||
      futureIncome.countries ||
      futureIncome.currencies ||
      [];
    impacts.income = incomeData.reduce(
      (acc: number, item: any) => acc + (item.amountBase || 0),
      0
    );
  }

  impacts.pnl = Object.values(impacts).reduce((acc, curr) => acc + curr);

  return impacts;
}

function getImpactsFromCoefficients(
  allocation: ScenarioCoefficientsGrouping | undefined,
  sliderValues: RiskImpacts,
  futureIncome?: any
) {
  const impacts: ImpactTotals & { pnl?: number; income?: number } = {
    price: 0,
    fx: 0,
    income: 0,
  };

  allocation?.entries.forEach((entry) => {
    impacts.price += (entry.priceMultiplier ?? 0) * (sliderValues.price ?? 0);
    impacts.price += (entry.yieldMultiplier ?? 0) * (sliderValues.yield ?? 0);
    impacts.fx += (entry.fxMultiplier ?? 0) * (sliderValues.fx ?? 0);
    // vol is always 0 if we're calculating from coefficients. The backend uses black scholes if there are options in the portfolio
  });

  // Calculate income impact from future income data
  if (futureIncome) {
    const incomeData =
      futureIncome.sectors ||
      futureIncome.assets ||
      futureIncome.countries ||
      futureIncome.currencies ||
      [];
    impacts.income = incomeData.reduce(
      (acc: number, item: any) => acc + (item.amountBase || 0),
      0
    );
  }

  impacts.pnl = Object.values(impacts).reduce((acc, curr) => acc + curr);
  return impacts;
}

export type RiskImpacts = {
  price: number;
  fx: number;
  yield: number;
  volatility: number;
};
export type ImpactTotals = {
  price: number;
  fx: number;
  income?: number;
  pnl?: number;
};
